import { classValidatorResolver } from "@hookform/resolvers/class-validator";
import { router } from "expo-router";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ScrollView, View } from "react-native";

import Button from "@/components/button/Button";
import Input from "@/components/input/Input";
import InputChoose from "@/components/input/InputChoose";
import InputDropList from "@/components/input/InputDropList";
import { ExternalAccountRequest } from "@/utils/dto/accounts.request";
import { BridgeBankAccountType } from "@/utils/dto/extracted_enums";
import { fetchLocation, LocationData } from "@/utils/location/Utils";

interface BankInformationProps {
  sendData: (data: FormData) => void;
}

type FormData = {
  accountType: string;
  accountNumber: string;
  routingNumber: string;
  bankName: string;
  bic?: string;
  bankCountry?: string;
};

export default function BankInformation(props: BankInformationProps) {
  const [accountType, setAccountType] = useState("us");
  const [locationData, setLocationData] = useState<LocationData>({
    countries: [],
    states: [],
    cities: [],
  });
  const { t } = useTranslation();

  const resolver = classValidatorResolver(ExternalAccountRequest, {
    validator: { skipMissingProperties: true },
  });

  const { control, handleSubmit, setValue } = useForm<FormData>({
    mode: "onSubmit",
    resolver,
  });

  const onSubmit = (data: FormData) => {
    props.sendData(data);
  };

  return (
    <ScrollView contentContainerClassName="w-full items-center bg-white1">
      <View className="flex-grow flex-col w-full items-center justify-center">
        <Input
          label={t("home/web3/externalaccounts:inputs.bankname")}
          control={control}
          name="bankName"
        />
        <View className="z-50">
          <InputChoose
            label={t("home/web3/externalaccounts:inputs.accounttype")}
            control={control}
            name="accountType"
            handleChange={(data) => setAccountType(data)}
            data={{ BridgeBankAccountType }}
          />
        </View>
        <Input
          label={t("home/web3/externalaccounts:inputs.accountnumber")}
          control={control}
          name="accountNumber"
        />
        {accountType === "us" ? (
          <Input
            label={t("home/web3/externalaccounts:inputs.routingnumber")}
            control={control}
            name="routingNumber"
          />
        ) : (
          <View className="z-40">
            <View className="z-50">
              <InputDropList
                label={t("home/web3/externalaccounts:inputs.bankcountry")}
                handleChange={(data) => {
                  setValue("bankCountry", data.code, { shouldValidate: true });
                  setLocationData({ ...locationData, countries: [] });
                }}
                clearValueFunction={() => {
                  setLocationData({ ...locationData, countries: [] });
                }}
                control={control}
                name="bankCountry"
                data={locationData.countries}
                onSearchChange={async (term) => {
                  try {
                    const data = await fetchLocation(term, "countries", "");
                    setLocationData({ ...locationData, countries: data });
                  } catch (error) {
                    console.error(`Error fetching countries:`, error);
                  }
                }}
                placeholder={t("common:placeholder.country")}
              />
            </View>
            <Input
              label={t("home/web3/externalaccounts:inputs.iban")}
              control={control}
              name="iban"
            />
          </View>
        )}
      </View>
      <Button
        label={t("common:button.nextstep")}
        onClick={handleSubmit(onSubmit)}
      />
    </ScrollView>
  );
}
